import { defineStore } from "pinia";
import { request, requestBool, requestRaw } from "@/api-connectors/basicRequests";
import { AxiosResponse } from "axios";

import { useSupplierStore } from "@/stores/suppliers";
import { useListStore } from "@/stores/lists";
import { useNotificationStore } from "@/stores/notification";

import { customizedErrorResponseHandler, handleAxiosError, isCustomizedErrorResponse } from "@/functions/handler";

import { PaginatedList } from "@/types/store";
import { AddMemberParams, Location, TeamMember, TeamMemberSetRoleParams, TeamRole } from "@/types/location";
import { SelectOption } from "@/types/others";

export interface LocationStore {
    activeLocation: Location | null
    locations: Location[] | null
    suppliers?: any[]
    team: {
        inviteCode?: string;
        members?: TeamMember[];
        roles?: TeamRole[];
    },
    lastLocationId: string;
}

export const useLocationStore = defineStore('location', {
    state: (): LocationStore => {
        return {
            activeLocation: null,
            locations: null,
            team: {},
            lastLocationId: ''
        }
    },
    actions: {
        clearStateVariables(): void {
            this.team = {}
        },

        getCachedValues(locationId: string): void {
            //const listStore = useListStore()
            //if (listStore.locationCache[locationId]) listStore.$patch(listStore.locationCache[locationId])
        },

        async createLocation(locationForm: Location): Promise<AxiosResponse | undefined> {
            const response = await requestRaw({
                method: 'POST',
                route: `locations`,
                body: locationForm
            }).catch(handleAxiosError)

            if (isCustomizedErrorResponse(response)) {
                await customizedErrorResponseHandler(response)
                return undefined
            }

            return response
        },

        async editLocation(locationForm: Location | { profilePictureId: string | null, id: string } | { id: string, locationType: string }): Promise<AxiosResponse | undefined> {
            const response = await requestRaw({
                method: 'PATCH',
                route: `locations/${locationForm.id}`,
                body: locationForm
            }).catch(handleAxiosError)

            if (isCustomizedErrorResponse(response)) {
                await customizedErrorResponseHandler(response)
                return undefined
            }

            this.activeLocation = response.data
            this.getLocationById(locationForm.id)

            return response
        },

        async deleteLocation(locationId: string): Promise<boolean> {
            const response = await requestBool({
                method: 'DELETE',
                route: `locations/${locationId}`,
            }, true)

            if (!response) return false

            this.activeLocation = null

            return true
        },

        async getLocations(dashboard?: boolean): Promise<AxiosResponse | undefined> {
            const response = await requestRaw({
                method: 'GET',
                route: `locations`,
                query: { dashboard }
            }).catch(handleAxiosError)

            if (isCustomizedErrorResponse(response)) {
                await customizedErrorResponseHandler(response)
                return undefined
            }

            this.locations = response.data?.data

            return response
        },

        async getLocationById(locationId: string): Promise<boolean> {
            const response = await request<Location>({
                method: 'GET',
                route: `locations/${locationId}`
            })

            if (!response) return false

            this.activeLocation = response

            return true
        },

        async getLocationSupplierImages(): Promise<boolean> {
            if (!this.locations) return true
            const supplierStore = useSupplierStore()
            const response = await Promise.all(this.locations.filter(location => !location.isDeleted).map(location => {
                return supplierStore.getLocationSupplierImage(location.id)
            }))

            return Promise.allSettled(response).then(results => {
                for (const result of results) {
                    if (result.status === 'rejected') return false
                }
                return true
            })
        },

        async getInvitationLink(locationId: string, role: string): Promise<boolean> {
            const response = await request<string>({
                method: 'POST',
                route: `locations/${locationId}/team/inviteLink`,
                body: {
                    role
                }
            }, true)

            if (!response) return false
            if (!this.team) this.team = {}

            this.team.inviteCode = response

            return true
        },

        async getTeamRoles(locationId: string): Promise<boolean> {
            const response = await request<PaginatedList<TeamRole>>({
                method: 'GET',
                route: `locations/${locationId}/team/roles`
            })

            if (!response) return false

            this.team.roles = response.data.filter(role => role.codeName !== 'OWNER')

            return true
        },

        async getTeam(locationId: string): Promise<boolean> {
            const response = await request<TeamMember[]>({
                method: 'GET',
                route: `locations/${locationId}/team`
            })

            if (!response) return false

            this.team.members = response

            return true
        },

        async addMember(params: AddMemberParams): Promise<boolean> {
            const { locationId, userId, role } = params

            const response = await requestBool({
                method: 'POST',
                route: `locations/${locationId}/team`,
                body: {
                    userId,
                    role
                }
            }, true)

            if (!response) return false

            await this.getTeam(locationId)

            return true
        },

        async redeemInvite(token: string): Promise<boolean> {
            return requestBool({
                method: 'POST',
                route: 'locations/redeemInvite',
                query: {
                    token
                }
            }, true)
        },

        async sendEmailInvite(locationId: string, role: string, email: string): Promise<boolean> {
            return requestBool({
                method: 'POST',
                route: `locations/${locationId}/team/sendInviteMail`,
                body: {
                    role,
                    email
                }
            })
        },

        async setRoleMember(params: TeamMemberSetRoleParams): Promise<boolean> {
            const { locationId, teamMemberId, role } = params

            const response = await requestBool({
                method: 'PUT',
                route: `locations/${locationId}/team/${teamMemberId}`,
                body: {
                    role
                }
            }, true)

            if (!response) return response

            this.getTeam(locationId)

            return response
        },

        async removeTeamMember(locationId: string, teamMemberId: string): Promise<boolean> {
            const response = await requestBool({
                method: 'DELETE',
                route: `locations/${locationId}/team/${teamMemberId}`
            }, true)


            if (!response) return false

            await this.getTeam(locationId)

            return true
        },
        setActiveLocation(locationId?: string): boolean {
            if (!locationId) return false

            const location: Location | undefined = this.locations?.find(location => location.id === locationId)

            if (!location) return false

            this.activeLocation = location
            return true
        },

        forcedLocationChange(locationId: string): void {
            const supplierStore = useSupplierStore()
            const listStore = useListStore()
            const notificationStore = useNotificationStore()
            const location = this.locations?.find(location => location.id === locationId)

            if (!location) return

            supplierStore.selectedSupplier = null
            supplierStore.catalog = {}
            listStore.lists = { data: [], count: 0 }
            listStore.cartLists = []
            listStore.isLoaded = {}
            notificationStore.promotions = { data: [], count: 0 }

            this.activeLocation = location
        }
    },
    getters: {
        isDeleted(state): boolean {
            return !!state.activeLocation?.isDeleted
        },
        sortedLocations(state): Location[] {
            return state.locations?.filter(location => !location.isDeleted)
                .sort((a, b) => a.name.localeCompare(b.name)) ?? []
        },
        deletedLocations(state): Location[] {
            return state.locations?.filter(location => location.isDeleted)
                .sort((a, b) => a.name.localeCompare(b.name)) ?? []
        },
        locationOptions(state): SelectOption[] {
            const alphabeticalSort: Location[] = state.locations?.filter(location => !location.isDeleted)
                .sort((a: Location, b: Location) => a.name.localeCompare(b.name)) ?? []
            return alphabeticalSort.sort(deletionSort).map<SelectOption>((location) => {
                return {
                    value: location.id,
                    text: location.name,
                    location
                }
            })
        }
    }
})

const deletionSort = (a: Location, b: Location): number => {
    if (!a.isDeleted && b.isDeleted) return -1
    if (a.isDeleted && !b.isDeleted) return 1
    return 0
}
